// Sentry subscription canceled. Keep user context sync disabled.
// import * as Sentry from "@sentry/nextjs";
import { autoLogin, checkExistingUser, logout } from "@src/api";
import ChannelService from "@src/api/channeltalk";
import { getCompanyInfo } from "@src/api/companies";
import { useResetApplyForm } from "@src/hook/applyHooks/useResetApplyForm";
import {
	clientCompanyInfoSelector,
	companyState,
	expertProfileSelector,
	globalSnackbarState,
	hasProfileDataSelector,
	initState,
	userInfoAtom,
	userState,
} from "@src/state";
import { UserInfo } from "@src/types/userInfo";
import { useRouter } from "next/router";
import { useMutation, useQueryClient } from "react-query";
import { useRecoilState, useSetRecoilState } from "recoil";
import useLoader from "./useLoader";

export interface User {
	isLogin: boolean;
	userId: string;
	userName: string;
	userType: number;
	channelTalkId: null;
	userPhone: string;
	userRoute: string;
	userRouteDetail: string;
	userNickname: string;
	position: string;
	introDate: string;
	userLevel: number;
	userUuid: string;
	refCode: string;
}

export interface ComapnyInfo {
	companyName: string;
	companySize: string;
}

const useUser = () => {
	const [user, setUser] = useRecoilState<User>(userState);
	const [userInfo, setUserInfo] = useRecoilState<UserInfo>(userInfoAtom);
	const [companyInfo, setCompanyInfo] = useRecoilState<ComapnyInfo>(companyState);
	const [hasProfile, setHasProfile] = useRecoilState(hasProfileDataSelector);
	const setExpertProfile = useSetRecoilState(expertProfileSelector);
	const setClientCompanyInfo = useSetRecoilState(clientCompanyInfoSelector);
	const setIsInit = useSetRecoilState(initState);
	const { handleResetApplyForm } = useResetApplyForm();
	const queryClient = useQueryClient();

	const router = useRouter();

	const withLoader = useLoader();

	const setMessage = useSetRecoilState(globalSnackbarState);

	const goApply = () => {
		if (user.userType === 1) {
			router.push("/match/my?st=1");
		} else {
			router.push("/expert-application");
		}
	};

	const getUserTypeResult = <T>(resultClient: T, resultExpert: T): T => {
		if (user.userType === 1) return resultClient;
		return resultExpert;
	};

	const getUserData = async () => {
		try {
			if (
				user.isLogin ||
				(!window.sessionStorage.getItem("accessToken") &&
					!window.localStorage.getItem("accessToken") &&
					!window.localStorage.getItem("refreshToken"))
			) {
				setIsInit(true);
				return;
			}
			const data = await autoLogin();
			const companyInfo = await getCompanyInfo();
			setUser({
				isLogin: true,
				userId: data.userId,
				userName: data.userName,
				userType: data.userType,
				channelTalkId: data.channelTalkId,
				userPhone: data.userPhone,
				userRoute: data.userRoute,
				userRouteDetail: data.userRouteDetail,
				userNickname: data.nickname,
				position: data.position,
				introDate: data.introDate,
				userLevel: companyInfo.companyLevel,
				userUuid: data.userUuid,
				refCode: data.refCode,
			});
			setUserInfo({
				name: data.userName,
				phone: data.userPhone,
				email: data.userId,
				companyName: companyInfo.companyName,
				industry: "",
				companySize: "",
				position: "",
				route: "",
				routeDetail: "",
				inviteCode: "",
			});
			setCompanyInfo({
				companyName: companyInfo.companyName,
				companySize: companyInfo.companySize,
			});
			// Sentry.setUser({
			// 	email: data.userId,
			// 	name: data.userName,
			// 	segment: data.userType,
			// });
			if (data.channelTalkId) {
				await ChannelService.shutdown();
				ChannelService.boot({
					pluginKey: process.env.NEXT_PUBLIC_CHANNEL_PLUGIN_KEY, //please fill with your plugin key
					memberId: data.channelTalkId,
				});
			}
			const profileData = await checkExistingUser();
			if (profileData) {
				setHasProfile(profileData.result);
				if (data.userType === 2 && profileData.expert) {
					setExpertProfile(profileData.expert);
				}
				if (data.userType === 1 && profileData.client) {
					setClientCompanyInfo(profileData.client);
				}
			}
			setIsInit(true);
			return profileData;
		} catch (error: any) {
			setUser({
				isLogin: false,
				userId: "",
				userName: "",
				userType: 0,
				channelTalkId: null,
				userPhone: "",
				userRoute: "",
				userRouteDetail: "",
				userNickname: "",
				position: "",
				introDate: "",
				userLevel: 0,
				userUuid: "",
				refCode: "",
			});
			setUserInfo({
				name: "",
				phone: "",
				email: "",
				companyName: "",
				industry: "",
				companySize: "",
				position: "",
				route: "",
				routeDetail: "",
				inviteCode: "",
			});
			setCompanyInfo({
				companyName: "",
				companySize: "",
			});
			setClientCompanyInfo({
				brandUrl: "",
				brandName: "",
				companyIndustry: "",
				companyName: "",
				companySize: "",
				companyType: "",
				roundOfInvestment: "",
			});
			// Sentry.setUser(null);
			setIsInit(true);
			setMessage(error.errorMessage);
			logout();
			return null;
		}
	};

	const loadData = async () => {
		await getUserData();
	};

	const { mutate: mutateGetUserData, isLoading: isLoadinGetUserData } = useMutation(
		"getUserData",
		loadData,
	);

	const logoutService = () => {
		const currentUserType = user.userType;
		handleResetApplyForm();
		setUser({
			isLogin: false,
			userId: "",
			userName: "",
			userType: 0,
			channelTalkId: null,
			userPhone: "",
			userRoute: "",
			userRouteDetail: "",
			userNickname: "",
			position: "",
			introDate: "",
			userLevel: 0,
			userUuid: "",
			refCode: "",
		});
		setUserInfo({
			name: "",
			phone: "",
			email: "",
			companyName: "",
			industry: "",
			companySize: "",
			position: "",
			route: "",
			routeDetail: "",
			inviteCode: "",
		});
		setCompanyInfo({
			companyName: "",
			companySize: "",
		});
		setExpertProfile({
			stage: "",
			stageId: null,
			stageDetail: null,
			uuid: "",
		});
		setClientCompanyInfo({
			brandUrl: "",
			brandName: "",
			companyIndustry: "",
			companyName: "",
			companySize: "",
			companyType: "",
			roundOfInvestment: "",
		});
		setHasProfile(false);
		// Sentry.setUser(null);
		logout();
		queryClient.clear();
		ChannelService.shutdown();
		if (currentUserType === 1) {
			router.push("/");
		} else {
			router.push("/expert");
		}
	};

	return {
		user,
		setUser,
		logoutService,
		getUserData,
		hasProfile,
		isLoadinGetUserData,
		mutateGetUserData,
		goApply,
		getUserTypeResult,
		companyInfo,
	};
};

export default useUser;
